import { useState, useEffect, useContext } from 'react';
import AuthContext from '../../../contexts/auth-context';
import useAxios from '../../../hooks/useAxios';
import { urls, toastify_status } from '../../config/variables';
import {
    Box,
    CircularProgress,
    Tooltip,
    Dialog,
    DialogContent,
    DialogTitle,
    Typography,
} from "@mui/material";
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import { FaFile } from "react-icons/fa";
import Toastify from '../Toastify';
// preview
import { Worker, Viewer } from "@react-pdf-viewer/core";
import pdfWorker from 'pdfjs-dist/build/pdf.worker.min.js';
import { toolbarPlugin } from "@react-pdf-viewer/toolbar";
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/toolbar/lib/styles/index.css";
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

const PreviewLabFileDialog = ({ cptIdToPreview, cptIdsToPreview, handleDeleteCptIdToPreview }) => {
    const authCtx = useContext(AuthContext);
    const token = authCtx.token;

    const { operation } = useAxios();

    // preview
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [previewType, setPreviewType] = useState(null); // pdf or image
    const [selectedCpt, setSelectedCpt] = useState(null); // ---> to store the selected cpt for download in the dialog.
    const [loadingPreview, setLoadingPreview] = useState(false); // ---> to show loading state while fetching the preview file.
    const [currentCptIndex, setCurrentCptIndex] = useState(0);

    // pdf viewer toolbar plugin instance
    const toolbarPluginInstance = toolbarPlugin();
    const { Toolbar } = toolbarPluginInstance;

    // preview
    const handlePreview = (cptId) => {
        setLoadingPreview(true);
        setPreviewOpen(true);
        setSelectedCpt(cptId); // ---> store the selected cpt to download the image at the dialog.
        setCurrentCptIndex(cptIdsToPreview.findIndex(rec => rec.cpt_id === cptId)); // ---> to set the current previewed file index for next and previous navigation.
        operation(
            {
                method: 'GET',
                url: urls.DOWNLOAD_LAB_RESULTS.replace(':id', cptId),
                headers: { Authorization: `token ${token}` },
                responseType: 'blob',
            },
            (fileResponse) => {
                const blob = new Blob([fileResponse], { type: fileResponse.type });
                const url = URL.createObjectURL(blob);

                if (fileResponse.type === 'application/pdf') {
                    setPreviewType('pdf');
                } else if (fileResponse.type.startsWith('image/')) {
                    setPreviewType('image');
                } else {
                    Toastify({
                        message: "Preview not supported for this file type",
                        state: toastify_status.ERROR,
                    });
                    URL.revokeObjectURL(url);
                    return;
                }

                setPreviewUrl(url);
                setLoadingPreview(false);
            },
            (err) => {
                setLoadingPreview(false);
                Toastify({
                    message: "Failed to load preview",
                    state: toastify_status.ERROR,
                });
            }
        );
    };

    const handleDownload = (cptId) => {
        operation(
            {
                method: 'GET',
                url: urls.DOWNLOAD_LAB_RESULTS.replace(':id', cptId),
                headers: { Authorization: `token ${token}` },
                responseType: 'blob',
            },
            (fileResponse) => {
                const blob = new Blob([fileResponse], { type: fileResponse.type });
                const url = window.URL.createObjectURL(blob);
                let extension = 'file';

                if (fileResponse.type === 'application/pdf') {
                    extension = 'pdf';
                } else if (fileResponse.type.startsWith('image/')) {
                    extension = fileResponse.type.split('/')[1]; // png, jpeg, jpg
                    if (extension === 'jpeg') extension = 'jpg';
                }

                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `lab-result.${extension}`);

                document.body.appendChild(link);
                link.click();
                link.remove();

                window.URL.revokeObjectURL(url);
            },
            (error) => {
                console.error('Download error:', error);
            }
        );
    };

    useEffect(() => {
        if (cptIdToPreview) {
            handlePreview(cptIdToPreview);
        }
        // eslint-disable-next-line
    }, [cptIdToPreview])


    const handlePrevious = () => {
        const currentIndex = cptIdsToPreview.findIndex(rec => rec.cpt_id === selectedCpt);
        if (currentIndex === 0) return;
        const nextIndex = currentIndex - 1
        const nextCptId = cptIdsToPreview[nextIndex].cpt_id;
        handlePreview(nextCptId, nextIndex); 
    }

    const handleNext = () => {
        const currentIndex = cptIdsToPreview.findIndex(rec => rec.cpt_id === selectedCpt);
        if (currentIndex === (cptIdsToPreview.length - 1)) return;
        const nextIndex = currentIndex + 1
        const nextCptId = cptIdsToPreview[nextIndex].cpt_id;
        handlePreview(nextCptId, nextIndex);
    }

    return (
        <Dialog
            open={previewOpen}
            onClose={() => {
                setPreviewOpen(false);
                if (previewUrl) URL.revokeObjectURL(previewUrl);
                setPreviewUrl(null);
                setPreviewType(null);
                handleDeleteCptIdToPreview()
            }}
            maxWidth="lg"
            fullWidth
        >
            <DialogTitle
            >
                <Box sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 1,
                }}>

                    {cptIdsToPreview && cptIdsToPreview.length > 0 && (
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                padding: "6px 16px",
                                backgroundColor: "#f5f7fa",
                                borderRadius: "4px",
                                boxShadow: "0 0 6px #f5f7fa",
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: "18px",
                                    fontWeight: 600,
                                    marginRight: "6px",
                                }}
                            >
                                Lab Results for
                            </Typography>

                            <Typography
                                sx={{
                                    fontSize: "18px",
                                    fontWeight: 600,
                                    color: "#4ABBA7",
                                }}
                            >
                                {cptIdsToPreview[0].patient_name}
                            </Typography>
                        </Box>
                    )}

                    <Box>
                        {previewType === 'image' && (
                            <Tooltip title='Download' arrow>
                                <IconButton
                                    onClick={() => {
                                        handleDownload(selectedCpt);
                                    }}
                                    sx={{ color: "grey.500" }}
                                >
                                    <DownloadIcon sx={{ fontSize: '28px' }} />
                                </IconButton>
                            </Tooltip>
                        )}
                        <Tooltip title='Close' arrow>
                            <IconButton
                                onClick={() => {
                                    setPreviewOpen(false);
                                    if (previewUrl) URL.revokeObjectURL(previewUrl);
                                    setPreviewUrl(null);
                                    setPreviewType(null);
                                    handleDeleteCptIdToPreview()
                                }}
                                sx={{ color: "grey.500" }}
                            >
                                <CloseIcon sx={{ fontSize: '28px' }} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>

                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "fit-content"
                    }}
                >
                    {cptIdsToPreview && cptIdsToPreview.length > 0 &&
                        selectedCpt &&
                        cptIdsToPreview.map((rec, index) => {
                            const isActive = selectedCpt === rec.cpt_id;
                            return (
                                <Tooltip title={`File ${index + 1}`} arrow key={rec.cpt_id}>
                                    <IconButton
                                        onClick={() => isActive ? null : handlePreview(rec.cpt_id, index)}
                                        size="small"
                                        sx={{
                                            borderRadius: 1,
                                            marginRight: '8px',
                                            backgroundColor: isActive ? "#4ABBA7" : "transparent",
                                            color: isActive ? "white" : "grey.600",
                                            transition: "all 0.2s ease",
                                            cursor: isActive ? "default" : "pointer",
                                            "&:hover": {
                                                backgroundColor: isActive ? "#3aa895" : "grey.200"
                                            }
                                        }}
                                    >
                                        <FaFile style={{ fontSize: '18px' }} />
                                    </IconButton>
                                </Tooltip>
                            );
                        })}
                </Box>
            </DialogTitle>


            <DialogContent sx={{ height: "80vh", p: 2, position: "relative" }}>
                {/* Left Arrow */}
                <IconButton
                    onClick={handlePrevious}
                    disabled={currentCptIndex === 0}
                    sx={{
                        position: "absolute",
                        left: 20,
                        top: "50%",
                        transform: "translateY(-50%)",
                        backgroundColor: "rgba(0,0,0,0.4)",
                        color: "white",
                        zIndex: 10,
                        "&:hover": {
                            backgroundColor: "rgba(0,0,0,0.6)",
                        }
                    }}
                >
                    <ArrowBackIosNewIcon sx={{ fontSize: '14px' }} />
                </IconButton>

                {/* Right Arrow */}
                <IconButton
                    onClick={handleNext}
                    disabled={currentCptIndex === (cptIdsToPreview.length - 1)}
                    sx={{
                        position: "absolute",
                        right: 20,
                        top: "50%",
                        transform: "translateY(-50%)",
                        backgroundColor: "rgba(0,0,0,0.4)",
                        color: "white",
                        zIndex: 10,
                        "&:hover": {
                            backgroundColor: "rgba(0,0,0,0.6)",
                        }
                    }}
                >
                    <ArrowForwardIosIcon sx={{ fontSize: '14px' }} />
                </IconButton>
                {loadingPreview ? (
                    <Box
                        sx={{
                            height: "100%",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            flexDirection: "column",
                            gap: 2,
                        }}
                    >
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        {/* PDF Preview */}
                        {previewType === "pdf" && previewUrl && (
                            <Worker workerUrl={pdfWorker}>
                                <Box
                                    sx={{
                                        height: "100%",
                                        display: "flex",
                                        flexDirection: "column",
                                    }}
                                >
                                    {/* Toolbar */}
                                        <Box
                                            sx={{
                                                borderBottom: "1px solid #e0e0e0",
                                                backgroundColor: "#fafafa",
                                            }}
                                        >
                                            <Toolbar>
                                                {(props) => {
                                                    const {
                                                        CurrentPageInput,
                                                        GoToNextPage,
                                                        GoToPreviousPage,
                                                        NumberOfPages,
                                                        ShowSearchPopover,
                                                        Download,
                                                        EnterFullScreen,
                                                    } = props;

                                                    return (
                                                        <Box
                                                            sx={{
                                                                display: "flex",
                                                                alignItems: "center",
                                                                gap: 2,
                                                                padding: "6px 12px",
                                                            }}
                                                        >
                                                             {/* toolbar plugins */}
                                                            <ShowSearchPopover />

                                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                                <CurrentPageInput />
                                                                <Typography variant="body2">
                                                                    / <NumberOfPages />
                                                                </Typography>
                                                            </Box>

                                                            <GoToPreviousPage />

                                                            <GoToNextPage />

                                                            <EnterFullScreen />

                                                            <Download />
                                                        </Box>
                                                    );
                                                }}
                                            </Toolbar>
                                        </Box>

                                    {/* PDF Viewer */}
                                    <Box sx={{ flex: 1, overflow: "hidden" }}>
                                        <Viewer
                                            fileUrl={previewUrl}
                                            plugins={[toolbarPluginInstance]}
                                        />
                                    </Box>
                                </Box>
                            </Worker>
                        )}

                        {/* Image Preview */}
                        {previewType === "image" && previewUrl && (
                            <Box
                                sx={{
                                    height: "100%",
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                }}
                            >
                                <img
                                    src={previewUrl}
                                    alt="Preview"
                                    style={{
                                        maxWidth: "100%",
                                        maxHeight: "100%",
                                        objectFit: "contain",
                                    }}
                                />
                            </Box>
                        )}
                    </>
                )}
            </DialogContent>

        </Dialog>
    )
}

export default PreviewLabFileDialog;